import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import GenericTabs from '../../../../ui-components/Tabs/GenericTabs';
import {
    TopologyData,
    TopologyNode,
    TopologyLink,
    BaselineData,
    filterDisplayableBaseline,
    computeDownLinks,
    getDisplayableBaselineLink,
    getRawBaselineLink,
    LinkLabelFields,
    DEFAULT_LINK_LABEL_FIELDS,
    LINK_LABEL_FIELD_NAMES,
    SptResult,
    toShortHostname,
    AlgoLinkMode,
    LinksByAlgoResponse,
    listAvailableAlgos,
    formatAlgoLabel,
    resolveAlgoLocatorName,
    linksByAlgoFromDdmDemands,
} from './TopologyData';
import { computeIsisSpt, extractP2pPath } from './utils/pathfinder';
import { computeAlgoHighlight } from './utils/algoFilter';
import { getTopology, getBaselineLatencies } from './request/request';
import TopologyGraph from './components/TopologyGraph';
import TopologyTableView, { TableSubTab } from './components/TopologyTableView';
import DownLinksAlarm from './components/DownLinksAlarm';
import NodeDetailPanel from './components/NodeDetailPanel';
import LinkDetailPanel from './components/LinkDetailPanel';
import LatencyPathAnalysis, { AnalysisMode } from './components/LatencyPathAnalysis';
import IsisMetricMatrix, { MatrixFilter } from './components/IsisMetricMatrix';
import LatencyMatrix from './components/LatencyMatrix';
import SiteLatencyMatrix from '../../SiteLatencyMatrix/SiteLatencyMatrix';
import type { LatencyPathMetric } from './utils/latencyMatrix';
import Tooltip from './components/Tooltip';
import SptDemoOverlay from './components/SptDemoOverlay';
import P2pPanel from './components/P2pModal';
import {
    Srv6DashboardTab,
    Srv6LatencyTab,
    Srv6DemandMatrixTab,
    Srv6HeatmapTab,
    Srv6AlertsTab,
    Srv6RecommendationsTab,
    ErrorBanner,
    getFilters,
    getCoverage,
    getDdm,
} from '../Srv6Insight';
import type { FilterOption } from '../Srv6Insight';

const VALID_TABS = [
    'graph',
    'table',
    'paths',
    'matrix',
    'latency-matrix',
    'traffic',
    'alerts',
    'recommendations',
] as const;
type ActiveTab = (typeof VALID_TABS)[number];
type TrafficSubTab = 'dashboard' | 'latency' | 'ddm' | 'heatmap';

function isActiveTab(value: string): value is ActiveTab {
    return (VALID_TABS as readonly string[]).includes(value);
}

const GRAPH_TAB = [
    { label: 'Graph', value: 'graph' },
];

const ISIS_TABS = [
    { label: 'Table', value: 'table' },
    { label: 'Paths', value: 'paths' },
    { label: 'Cost Matrix', value: 'matrix' },
    { label: 'Latency Matrix', value: 'latency-matrix' },
];

const SRV6_TAB_LIST = [
    { label: 'Traffic', value: 'traffic' },
    { label: 'Alerts', value: 'alerts' },
    { label: 'Recommendations', value: 'recommendations' },
];

const TABLE_SUB_TABS = [
    { label: 'Nodes', value: 'nodes' },
    { label: 'Links', value: 'links' },
];

const PATHS_SUB_TABS = [
    { label: 'Site to Site', value: 'site' },
    { label: 'Router to Router', value: 'router' },
];

const MATRIX_SUB_TABS = [
    { label: 'All', value: 'all' },
    { label: 'Asymmetric Only', value: 'asymmetric' },
];

type LatencyMatrixView = 'router' | 'site';

const LATENCY_VIEW_TABS: { label: string; value: LatencyMatrixView }[] = [
    { label: 'By router', value: 'router' },
    { label: 'By site', value: 'site' },
];

const LATENCY_METRIC_TABS: { label: string; value: LatencyPathMetric }[] = [
    { label: 'IGP cost (DEFAULT)', value: 'igp' },
    { label: 'Min delay (LATENCY)', value: 'min-delay' },
];

const TRAFFIC_SUB_TABS = [
    { label: 'Distribution', value: 'dashboard' },
    { label: 'Link Health', value: 'latency' },
    { label: 'Demand Matrix', value: 'ddm' },
    { label: 'Heatmap', value: 'heatmap' },
];

const SRV6_TABS = new Set<ActiveTab>(['traffic', 'alerts', 'recommendations']);

const TAB_TOOLTIPS: Record<string, string> = {
    graph: 'Interactive IS-IS topology graph. Pan, zoom, click routers and links to inspect details.',
    table: 'Sortable table of all routers and links with key metrics (latency, bandwidth, loss).',
    paths: 'Compute and compare all available latency paths between two routers or two sites.',
    matrix: 'IS-IS metric cost matrix between every pair of routers in the domain.',
    'latency-matrix': 'One-way delay between every pair of routers, summed link by link along the path SPF would choose.',
    traffic: 'SRv6 traffic distribution, link health, demand matrix, and heatmap.',
    alerts: 'Active SRv6 alerts: latency spikes, packet loss, and capacity threshold breaches.',
    recommendations: 'Recommendations for traffic engineering and capacity planning.',
};

const TABLE_TOOLTIPS: Record<string, string> = {
    nodes: 'Router inventory with datacenter, role, type, and local status.',
    links: 'All IS-IS adjacencies with latency, bandwidth, metric, and baseline deviation.',
};

const PATHS_TOOLTIPS: Record<string, string> = {
    site: 'Compute all latency paths between two sites (datacenter groups).',
    router: 'Compute all latency paths between two individual routers.',
};

const MATRIX_TOOLTIPS: Record<string, string> = {
    all: 'Show the full metric cost matrix for all routers.',
    asymmetric: 'Show only routers with asymmetric IS-IS costs (A->B differs from B->A).',
};

const LATENCY_VIEW_TOOLTIPS: Record<LatencyMatrixView, string> = {
    router: 'One-way delay between every pair of routers, with a choice of path metric.',
    site: 'Round-trip time between sites, exactly as partners see it under Network monitoring → Latency: lowest among the routers of each site, IGP path only.',
};

const LATENCY_METRIC_TOOLTIPS: Record<LatencyPathMetric, string> = {
    igp: 'Path by IS-IS cost (algo 0, DEFAULT) — what most customer services ride. Equal-cost paths show as a range.',
    'min-delay': 'Path by advertised min link delay (algo 128, LATENCY) — the fastest path the network offers.',
};

const TRAFFIC_TOOLTIPS: Record<string, string> = {
    dashboard: 'Traffic distribution per SRv6 algorithm (Algo 0, 128, 129, etc.).',
    latency: 'Per-interface jitter, delay, and packet loss trends. Click table rows to compare links.',
    ddm: 'Source-to-destination demand matrix showing traffic volumes.',
    heatmap: 'Color-coded heatmap of traffic utilization across all router pairs.',
};

const TopologyPage: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [searchParams, setSearchParams] = useSearchParams();

    const [topologyData, setTopologyData] =
        useState<TopologyData | null>(null);
    const [baselineData, setBaselineData] =
        useState<BaselineData | null>(null);
    const [rawBaselineData, setRawBaselineData] =
        useState<BaselineData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<ActiveTab>('graph');

    // Detail panel state (shared between graph and table)
    const [selectedNode, setSelectedNode] =
        useState<TopologyNode | null>(null);
    const [selectedLink, setSelectedLink] =
        useState<TopologyLink | null>(null);

    // Path analysis state
    const [pathSource, setPathSource] = useState<string | null>(null);
    const [pathTarget, setPathTarget] = useState<string | null>(null);
    const [pathSiteSrc, setPathSiteSrc] = useState<string | null>(null);
    const [pathSiteTgt, setPathSiteTgt] = useState<string | null>(null);
    const [pathMode, setPathMode] = useState(false);

    // SPT mode state
    const [sptMode, setSptMode] = useState(false);
    const [sptResult, setSptResult] = useState<SptResult | null>(null);

    // Simulation mode state
    const [simulationMode, setSimulationMode] = useState(false);
    const [excludedLinks, setExcludedLinks] = useState<Set<string>>(new Set());
    const [excludedNodes, setExcludedNodes] = useState<Set<string>>(new Set());

    // Point-to-point path overlay
    const [p2pSource, setP2pSource] = useState<string | null>(null);
    const [p2pTarget, setP2pTarget] = useState<string | null>(null);
    const [p2pEdgeIds, setP2pEdgeIds] = useState<Set<string> | null>(null);
    const [p2pNodeIds, setP2pNodeIds] = useState<Set<string> | null>(null);

    // Traffic overlay on graph edges
    const [trafficOverlay, setTrafficOverlay] = useState(false);
    // Packet-loss overlay on graph edges (mutually exclusive with trafficOverlay)
    const [packetLossOverlay, setPacketLossOverlay] = useState(false);

    // ── Flex-Algo / SRv6 Locator filter ────────────────────────────
    // `selectedAlgo` is the IS-IS algorithm number (stable key). UI
    // labels resolve through `srv6Locators[].locatorName` when the
    // backend has enriched the payload.
    //
    // `algoLinkMode` controls Level 2 highlighting:
    //   - 'off'     → only Level 1 (dim non-participating nodes)
    //   - 'traffic' → join SRv6 DDM demands against the topology
    //   - 'spf'     → run Dijkstra per algo (graceful: IGP fallback
    //                 when flexAlgoDefinitions are not yet serialized)
    //   - 'both'    → overlay both sets in one pass
    const [selectedAlgo, setSelectedAlgo] = useState<number | null>(null);
    const [algoLinkMode, setAlgoLinkMode] = useState<AlgoLinkMode>('off');
    const [algoLinksTraffic, setAlgoLinksTraffic] =
        useState<LinksByAlgoResponse | null>(null);
    const [algoLinksLoading, setAlgoLinksLoading] = useState(false);
    const [algoLinksError, setAlgoLinksError] = useState<string | null>(null);

    // SRv6 Insight state (shared across Traffic / Alerts / Recommendations tabs)
    const [srv6Sources, setSrv6Sources] = useState<FilterOption[]>([]);
    const [srv6SelectedSource, setSrv6SelectedSource] = useState('');
    const [srv6TimeRange, setSrv6TimeRange] = useState('1h');
    const [trafficSubTab, setTrafficSubTab] = useState<TrafficSubTab>('dashboard');
    const [srv6Coverage, setSrv6Coverage] = useState<{ active: number; total: number } | null>(null);
    const [tableSubTab, setTableSubTab] = useState<TableSubTab>('nodes');
    const [pathsMode, setPathsMode] = useState<AnalysisMode>('site');
    const [matrixFilter, setMatrixFilter] = useState<MatrixFilter>('all');
    const [latencyMetric, setLatencyMetric] = useState<LatencyPathMetric>('igp');
    const [latencyView, setLatencyView] = useState<LatencyMatrixView>('router');

    // P2P modal (inline popup from Demand Matrix "View in topo")
    const [p2pModalPair, setP2pModalPair] = useState<{ source: string; target: string } | null>(null);

    // SPT Demo mode
    const [demoMode, setDemoMode] = useState(false);

    // Link card field visibility
    const [linkFields, setLinkFields] = useState<LinkLabelFields>(
        DEFAULT_LINK_LABEL_FIELDS
    );
    const [showFieldMenu, setShowFieldMenu] = useState(false);
    const fieldMenuRef = useRef<HTMLDivElement>(null);

    // Close menu on outside click
    useEffect(() => {
        if (!showFieldMenu) return;
        const handler = (e: MouseEvent) => {
            if (
                fieldMenuRef.current &&
                !fieldMenuRef.current.contains(e.target as Node)
            ) {
                setShowFieldMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showFieldMenu]);

    const toggleField = useCallback((field: keyof LinkLabelFields) => {
        setLinkFields((prev) => ({ ...prev, [field]: !prev[field] }));
    }, []);

    const handleSptRootSelect = useCallback(
        (nodeId: string) => {
            if (!topologyData) return;
            const result = computeIsisSpt(
                topologyData.links,
                nodeId,
                excludedLinks.size > 0 ? excludedLinks : undefined,
                excludedNodes.size > 0 ? excludedNodes : undefined
            );
            setSptResult(result);
        },
        [topologyData, excludedLinks, excludedNodes]
    );

    const handleToggleExcludeLink = useCallback((linkId: string) => {
        setExcludedLinks((prev) => {
            const next = new Set(prev);
            if (next.has(linkId)) next.delete(linkId);
            else next.add(linkId);
            return next;
        });
    }, []);

    const handleToggleExcludeNode = useCallback((nodeId: string) => {
        setExcludedNodes((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) next.delete(nodeId);
            else next.add(nodeId);
            return next;
        });
    }, []);

    const sptRootRef = useRef<string | null>(null);

    // Keep ref in sync with current SPT root
    useEffect(() => {
        sptRootRef.current = sptResult?.root ?? null;
    }, [sptResult]);

    // Recompute SPT when exclusions change and SPT is active
    useEffect(() => {
        const root = sptRootRef.current;
        if (!root || !topologyData) return;
        const updated = computeIsisSpt(
            topologyData.links,
            root,
            excludedLinks.size > 0 ? excludedLinks : undefined,
            excludedNodes.size > 0 ? excludedNodes : undefined
        );
        setSptResult(updated);
    }, [excludedLinks, excludedNodes, topologyData]);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const token = await getAccessTokenSilently();

            const [topoResp, baseResp] = await Promise.allSettled([
                getTopology(token),
                getBaselineLatencies(token),
            ]);

            if (
                topoResp.status === 'fulfilled' &&
                topoResp.value.data?.success
            ) {
                setTopologyData(topoResp.value.data.data);
            } else {
                setError('Failed to load topology data');
            }

            if (
                baseResp.status === 'fulfilled' &&
                baseResp.value.data?.success
            ) {
                const raw = baseResp.value.data.data as BaselineData;
                setRawBaselineData(raw);
                setBaselineData(filterDisplayableBaseline(raw));
            }
        } catch (err: unknown) {
            const e = err as Record<string, unknown>;
            const resp = e?.response as Record<string, unknown> | undefined;
            const data = resp?.data as Record<string, unknown> | undefined;
            setError(
                (data?.message as string) ||
                    (e?.message as string) ||
                    'Failed to load topology'
            );
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Load SRv6 source filter options once
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const res = await getFilters(token);
                if (cancelled) return;
                if (res.data?.data) {
                    const srcs = res.data.data.sources || [];
                    setSrv6Sources(srcs);
                    if (srcs.length > 0) setSrv6SelectedSource(srcs[0].value);
                }
            } catch {
                // non-critical
            }
        })();
        return () => { cancelled = true; };
    }, [getAccessTokenSilently]);

    // Load SRv6 coverage (active vs total reporting routers)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const res = await getCoverage(token);
                if (cancelled) return;
                if (res.data?.data) setSrv6Coverage(res.data.data);
            } catch {
                // non-critical
            }
        })();
        return () => { cancelled = true; };
    }, [getAccessTokenSilently]);

    // ── Flex-Algo deep link (?algo=130&algoMode=traffic) ───────────
    // Restore the algo filter from the URL on first topology load.
    // Only `algorithm` numbers actually present in the payload are
    // applied — a stale link from a prior deploy silently no-ops.
    const algoUrlApplied = useRef(false);
    useEffect(() => {
        if (algoUrlApplied.current || !topologyData) return;
        const qAlgo = searchParams.get('algo');
        const qMode = searchParams.get('algoMode');
        if (!qAlgo) return;

        const algoNum = Number(qAlgo);
        if (!Number.isFinite(algoNum)) return;

        const exists = topologyData.nodes.some((n) =>
            n.srv6Locators.some((l) => l.algorithm === algoNum)
        );
        if (!exists) return;

        algoUrlApplied.current = true;
        setSelectedAlgo(algoNum);
        const validModes: AlgoLinkMode[] = ['off', 'traffic', 'spf', 'both'];
        if (qMode && validModes.includes(qMode as AlgoLinkMode)) {
            setAlgoLinkMode(qMode as AlgoLinkMode);
        }
        searchParams.delete('algo');
        searchParams.delete('algoMode');
        setSearchParams(searchParams, { replace: true });
    }, [topologyData, searchParams, setSearchParams]);

    // Fetch real-traffic link set from SRv6 DDM whenever the user picks
    // an algo and the active mode consumes it. Demands are collapsed to
    // unique (source, interfaceOut) rows and joined to topology links.
    useEffect(() => {
        if (
            selectedAlgo == null ||
            (algoLinkMode !== 'traffic' && algoLinkMode !== 'both')
        ) {
            setAlgoLinksTraffic(null);
            setAlgoLinksError(null);
            setAlgoLinksLoading(false);
            return;
        }
        let cancelled = false;
        setAlgoLinksLoading(true);
        setAlgoLinksError(null);
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const locatorName = resolveAlgoLocatorName(
                    selectedAlgo,
                    topologyData?.algoMap
                );
                const res = await getDdm(token, {
                    algo: locatorName,
                    timeRange: srv6TimeRange,
                });
                if (cancelled) return;
                const payload = res.data?.data;
                if (payload?.demands) {
                    setAlgoLinksTraffic(
                        linksByAlgoFromDdmDemands(
                            selectedAlgo,
                            locatorName,
                            payload.timeRange ?? srv6TimeRange,
                            payload.demands
                        )
                    );
                } else {
                    setAlgoLinksTraffic(null);
                    setAlgoLinksError('No traffic data returned');
                }
            } catch {
                if (cancelled) return;
                setAlgoLinksTraffic(null);
                setAlgoLinksError('Failed to load traffic data');
            } finally {
                if (!cancelled) setAlgoLinksLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        selectedAlgo,
        algoLinkMode,
        srv6TimeRange,
        topologyData?.algoMap,
        getAccessTokenSilently,
    ]);

    const availableAlgos = useMemo(
        () => (topologyData ? listAvailableAlgos(topologyData) : []),
        [topologyData]
    );

    // Single source of truth for what the renderer should dim/colour.
    // Returns stable empty sets when the filter is inactive, so the
    // graph behaves identically to the pre-filter version.
    const algoHighlight = useMemo(() => {
        if (!topologyData) return null;
        return computeAlgoHighlight(
            topologyData,
            selectedAlgo,
            algoLinkMode,
            algoLinksTraffic
        );
    }, [topologyData, selectedAlgo, algoLinkMode, algoLinksTraffic]);

    const handleAlgoChange = useCallback((algo: number | null) => {
        setSelectedAlgo(algo);
        // Picking an algo from "All" defaults to Level-1 highlight only
        // (mode `off`). Clearing the algo also resets the link mode so
        // we don't keep a stale "traffic" pill ghost-active.
        if (algo == null) setAlgoLinkMode('off');
    }, []);

    // Auto-trigger point-to-point path from URL query params (?source=X&target=Y)
    const deepLinkApplied = useRef(false);
    useEffect(() => {
        if (deepLinkApplied.current || !topologyData) return;
        const qSource = searchParams.get('source');
        const qTarget = searchParams.get('target');
        if (!qSource || !qTarget) return;

        const resolve = (term: string) => {
            const lower = term.toLowerCase();
            return topologyData.nodes.find(
                (n) =>
                    n.id.toLowerCase() === lower ||
                    n.hostname.toLowerCase() === lower ||
                    toShortHostname(n.hostname) === lower
            );
        };

        const srcNode = resolve(qSource);
        const tgtNode = resolve(qTarget);
        if (srcNode && tgtNode && srcNode.id !== tgtNode.id) {
            deepLinkApplied.current = true;
            setActiveTab('graph');

            const spt = computeIsisSpt(topologyData.links, srcNode.id);
            setSptMode(true);
            setSptResult(spt);

            const p2p = extractP2pPath(spt, tgtNode.id);
            if (p2p) {
                setP2pSource(srcNode.id);
                setP2pTarget(tgtNode.id);
                setP2pEdgeIds(p2p.edgeIds);
                setP2pNodeIds(p2p.nodeIds);
            }

            setPathSource(srcNode.id);
            setPathTarget(tgtNode.id);
            searchParams.delete('source');
            searchParams.delete('target');
            setSearchParams(searchParams, { replace: true });
        }
    }, [topologyData, searchParams, setSearchParams]);

    const handleNodeClick = useCallback(
        (nodeId: string) => {
            setSelectedLink(null);
            const node = topologyData?.nodes.find(
                (n) => n.id === nodeId
            );
            setSelectedNode(node ?? null);
        },
        [topologyData]
    );

    // Links tracked in baseline but gone from the current IS-IS snapshot
    // (adjacency fully dropped). Rendered with the offline treatment on the
    // graph and table; kept out of topologyData.links so path/SPT/summary
    // computations stay based on the live snapshot only.
    const downLinks = useMemo(
        () => computeDownLinks(rawBaselineData, topologyData),
        [rawBaselineData, topologyData]
    );

    const handleEdgeClick = useCallback(
        (linkId: string) => {
            setSelectedNode(null);
            const link =
                topologyData?.links.find((l) => l.id === linkId) ??
                downLinks.find((l) => l.id === linkId);
            setSelectedLink(link ?? null);
        },
        [topologyData, downLinks]
    );

    const handleClosePanel = useCallback(() => {
        setSelectedNode(null);
        setSelectedLink(null);
    }, []);

    // Support ?tab= query param for deep-linking to SRv6 tabs
    const tabParamApplied = useRef(false);
    useEffect(() => {
        if (tabParamApplied.current) return;
        const qTab = searchParams.get('tab');
        if (!qTab) return;
        if (isActiveTab(qTab)) {
            tabParamApplied.current = true;
            setActiveTab(qTab);
            searchParams.delete('tab');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const clearP2p = useCallback(() => {
        setP2pSource(null);
        setP2pTarget(null);
        setP2pEdgeIds(null);
        setP2pNodeIds(null);
    }, []);

    // Close detail panel when switching tabs
    const handleTabChange = useCallback((tab: ActiveTab) => {
        setActiveTab(tab);
        setSelectedNode(null);
        setSelectedLink(null);
        if (tab !== 'graph') {
            setPathMode(false);
            setSptMode(false);
            setSptResult(null);
            setSimulationMode(false);
            clearP2p();
        }
    }, [clearP2p]);

    // Navigate from alerts/recommendations to a specific tab + source filter
    const handleAlertNavigate = useCallback((tab: string, sourceShort?: string) => {
        const target = isActiveTab(tab) ? tab : 'traffic';
        handleTabChange(target);
        if (target === 'traffic') setTrafficSubTab('dashboard');
        if (sourceShort) {
            const match = srv6Sources.find(
                (s) => s.label === sourceShort || s.value === sourceShort
            );
            if (match) setSrv6SelectedSource(match.value);
        }
    }, [handleTabChange, srv6Sources]);

    // Handle router path selection from graph (2-click mode)
    const handlePathSelect = useCallback(
        (source: string, target: string) => {
            setPathSource(source);
            setPathTarget(target);
            setPathSiteSrc(null);
            setPathSiteTgt(null);
            setPathMode(false);
            setActiveTab('paths');
        },
        []
    );

    // Handle site path selection from graph (2-click on site groups)
    const handleSitePathSelect = useCallback(
        (sourceSite: string, targetSite: string) => {
            setPathSiteSrc(sourceSite);
            setPathSiteTgt(targetSite);
            setPathSource(null);
            setPathTarget(null);
            setPathMode(false);
            setActiveTab('paths');
        },
        []
    );

    const handleDemoClose = useCallback(() => {
        setDemoMode(false);
        setSptMode(false);
        setSptResult(null);
        setSimulationMode(false);
        setExcludedLinks(new Set());
        setExcludedNodes(new Set());
        setTrafficOverlay(false);
        clearP2p();
    }, [clearP2p]);

    const baselineForLink = selectedLink
        ? getDisplayableBaselineLink(baselineData, selectedLink.id)
        : null;
    const rawBaselineForLink = selectedLink
        ? getRawBaselineLink(rawBaselineData, selectedLink.id)
        : null;

    const graphTabWithTip = useMemo(() => GRAPH_TAB.map((t) => ({
        ...t,
        label: <Tooltip text={TAB_TOOLTIPS[t.value] || ''} position="bottom">{t.label}</Tooltip>,
    })), []);

    const isisTabsWithTips = useMemo(() => ISIS_TABS.map((t) => ({
        ...t,
        label: <Tooltip text={TAB_TOOLTIPS[t.value] || ''} position="bottom">{t.label}</Tooltip>,
    })), []);

    const srv6TabsWithTips = useMemo(() => SRV6_TAB_LIST.map((t) => ({
        ...t,
        label: <Tooltip text={TAB_TOOLTIPS[t.value] || ''} position="bottom">{t.label}</Tooltip>,
    })), []);

    const tableTabsWithTips = useMemo(() => TABLE_SUB_TABS.map((t) => ({
        ...t,
        label: <Tooltip text={TABLE_TOOLTIPS[t.value] || ''} position="bottom">{t.label}</Tooltip>,
    })), []);

    const pathsTabsWithTips = useMemo(() => PATHS_SUB_TABS.map((t) => ({
        ...t,
        label: <Tooltip text={PATHS_TOOLTIPS[t.value] || ''} position="bottom">{t.label}</Tooltip>,
    })), []);

    const matrixTabsWithTips = useMemo(() => MATRIX_SUB_TABS.map((t) => ({
        ...t,
        label: <Tooltip text={MATRIX_TOOLTIPS[t.value] || ''} position="bottom">{t.label}</Tooltip>,
    })), []);

    const latencyViewTabsWithTips = useMemo(() => LATENCY_VIEW_TABS.map((t) => ({
        ...t,
        label: <Tooltip text={LATENCY_VIEW_TOOLTIPS[t.value]} position="bottom">{t.label}</Tooltip>,
    })), []);

    const latencyMetricTabsWithTips = useMemo(() => LATENCY_METRIC_TABS.map((t) => ({
        ...t,
        label: <Tooltip text={LATENCY_METRIC_TOOLTIPS[t.value]} position="bottom">{t.label}</Tooltip>,
    })), []);

    const trafficTabsWithTips = useMemo(() => TRAFFIC_SUB_TABS.map((t) => ({
        ...t,
        label: <Tooltip text={TRAFFIC_TOOLTIPS[t.value] || ''} position="bottom">{t.label}</Tooltip>,
    })), []);

    const isSrv6Tab = SRV6_TABS.has(activeTab);

    // Loading state
    if (isLoading) {
        return (
            <div className="flex flex-col h-full p-4 gap-4">
                <div className="h-16 rounded-lg bg-semantic-bg-secondary animate-pulse" />
                <div className="h-8 w-48 rounded-lg bg-semantic-bg-secondary animate-pulse" />
                <div className="flex-1 rounded-lg bg-semantic-bg-secondary animate-pulse" />
            </div>
        );
    }

    // Error state
    if (error || !topologyData) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-4">
                <ErrorBanner
                    message={error || 'No topology data available'}
                    onRetry={fetchData}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full p-4 gap-3">
            {/* Categorized tab row: Graph | ISIS | SRv6 + contextual controls */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-end gap-3">
                    <GenericTabs
                        tabs={graphTabWithTip}
                        activeTab={activeTab}
                        onTabChange={(v) => handleTabChange(v as ActiveTab)}
                        size="xs"
                        fullWidth={false}
                        aria-label="Graph view"
                    />
                    <div className="w-px h-7 bg-semantic-border-secondary" />
                    <div className="flex flex-col gap-0.5">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-semantic-text-secondary/50 pl-1">ISIS</span>
                        <GenericTabs
                            tabs={isisTabsWithTips}
                            activeTab={activeTab}
                            onTabChange={(v) => handleTabChange(v as ActiveTab)}
                            size="xs"
                            fullWidth={false}
                            aria-label="IS-IS views"
                        />
                    </div>
                    <div className="w-px h-7 bg-semantic-border-secondary" />
                    <div className="flex flex-col gap-0.5">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-semantic-text-secondary/50 pl-1">SRv6</span>
                        <GenericTabs
                            tabs={srv6TabsWithTips}
                            activeTab={activeTab}
                            onTabChange={(v) => handleTabChange(v as ActiveTab)}
                            size="xs"
                            fullWidth={false}
                            aria-label="SRv6 views"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3 ml-auto text-xs text-semantic-text-secondary">
                    <span><span className="font-semibold text-semantic-text-brand">{topologyData.topology.summary.totalSites}</span> sites</span>
                    <span><span className="font-semibold text-semantic-text-brand">{topologyData.topology.summary.totalNodes}</span> nodes</span>
                    <DownLinksAlarm
                        downLinks={downLinks}
                        rawBaseline={rawBaselineData}
                        onSelectLink={handleEdgeClick}
                    />
                    {isSrv6Tab && srv6Coverage && (
                        <Tooltip text="Routers actively sending SRv6 counters vs total ISIS routers." position="bottom">
                            <span>
                                Reporting: <span className="font-semibold text-semantic-text-brand">{srv6Coverage.active}</span>
                                {' / '}{srv6Coverage.total}
                            </span>
                        </Tooltip>
                    )}

                    {/* SRv6 filters — visible when any SRv6 tab is active */}
                    {isSrv6Tab && activeTab === 'traffic' && (trafficSubTab === 'dashboard' || trafficSubTab === 'latency') && (
                        <Tooltip text="Filter data by SRv6 source router." position="bottom">
                            <select
                                value={srv6SelectedSource}
                                onChange={(e) => setSrv6SelectedSource(e.target.value)}
                                aria-label="SRv6 source filter"
                                className="px-3 py-1.5 text-xs border rounded-lg bg-semantic-bg-primary
                                           text-semantic-text-primary border-semantic-border-secondary"
                            >
                                {srv6Sources.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </Tooltip>
                    )}
                    {isSrv6Tab && (
                        <Tooltip text="Time window for SRv6 data queries." position="bottom">
                            <select
                                value={srv6TimeRange}
                                onChange={(e) => setSrv6TimeRange(e.target.value)}
                                aria-label="Time range"
                                className="px-3 py-1.5 text-xs border rounded-lg bg-semantic-bg-primary
                                           text-semantic-text-primary border-semantic-border-secondary"
                            >
                                <option value="5m">5 min</option>
                                <option value="15m">15 min</option>
                                <option value="1h">1 hour</option>
                                <option value="6h">6 hours</option>
                                <option value="24h">24 hours</option>
                            </select>
                        </Tooltip>
                    )}

                    {/* Graph-specific: Label Fields menu */}
                    {activeTab === 'graph' && (
                        <div className="relative" ref={fieldMenuRef}>
                            <Tooltip text="Choose which metrics are displayed on link cards.">
                                <button
                                    onClick={() => setShowFieldMenu((v) => !v)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs
                                        font-semibold rounded-lg border transition-all
                                        duration-150 ${
                                            showFieldMenu
                                                ? 'border-semantic-border-brand bg-semantic-bg-brand-primary text-semantic-text-brand'
                                                : 'border-semantic-border-secondary text-semantic-text-secondary hover:text-semantic-text-primary'
                                        }`}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="4" y1="6" x2="20" y2="6" />
                                        <line x1="4" y1="12" x2="20" y2="12" />
                                        <line x1="4" y1="18" x2="20" y2="18" />
                                        <circle cx="8" cy="6" r="2" fill="currentColor" />
                                        <circle cx="16" cy="12" r="2" fill="currentColor" />
                                        <circle cx="10" cy="18" r="2" fill="currentColor" />
                                    </svg>
                                    Labels
                                    <span
                                        className="inline-flex items-center justify-center rounded-full
                                            text-[10px] font-bold leading-none min-w-[16px] h-[16px] px-0.5"
                                        style={{
                                            background: 'var(--color-semantic-bg-brand-primary)',
                                            color: 'var(--color-semantic-text-brand)',
                                        }}
                                    >
                                        {Object.values(linkFields).filter(Boolean).length}
                                    </span>
                                </button>
                            </Tooltip>
                            {showFieldMenu && (
                                <div className="absolute top-full right-0 mt-1 z-50
                                    bg-semantic-bg-primary border border-semantic-border-secondary
                                    rounded-xl shadow-xl min-w-[210px] overflow-hidden">
                                    <div className="px-3 py-2 border-b border-semantic-border-secondary
                                        flex items-center justify-between">
                                        <span className="text-[11px] font-semibold text-semantic-text-secondary
                                            tracking-wide uppercase">
                                            Visible on cards
                                        </span>
                                        <button
                                            className="text-[10px] font-semibold text-semantic-text-brand
                                                hover:underline cursor-pointer bg-transparent border-none p-0"
                                            onClick={() => {
                                                const allOn = Object.values(linkFields).every(Boolean);
                                                const next = {} as LinkLabelFields;
                                                for (const k of Object.keys(linkFields) as (keyof LinkLabelFields)[]) {
                                                    next[k] = !allOn;
                                                }
                                                setLinkFields(next);
                                            }}
                                        >
                                            {Object.values(linkFields).every(Boolean) ? 'Hide all' : 'Show all'}
                                        </button>
                                    </div>
                                    <div className="py-1">
                                        {(Object.keys(LINK_LABEL_FIELD_NAMES) as (keyof LinkLabelFields)[]).map((key) => (
                                            <label key={key} className="flex items-center gap-2.5 px-3 py-1.5
                                                text-xs text-semantic-text-primary hover:bg-semantic-bg-secondary
                                                cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={linkFields[key]}
                                                    onChange={() => toggleField(key)}
                                                    className="rounded accent-current"
                                                />
                                                {LINK_LABEL_FIELD_NAMES[key]}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Secondary toolbar: context-dependent controls per active tab */}
            {activeTab === 'graph' && (
                <div className="flex items-center gap-2 flex-wrap">
                    <Tooltip text="Click two routers (or two site groups) to compute all latency paths between them.">
                        <button
                            onClick={() => setPathMode(!pathMode)}
                            aria-pressed={pathMode}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg
                                border transition-all duration-150 ${
                                    pathMode
                                        ? 'border-semantic-border-brand bg-semantic-bg-brand-primary text-semantic-text-brand'
                                        : 'border-semantic-border-secondary text-semantic-text-secondary hover:text-semantic-text-primary'
                                }`}
                        >
                            {pathMode ? 'Path: Click 2 nodes' : 'Select Path'}
                        </button>
                    </Tooltip>
                    <Tooltip text="Shortest Path Tree: click a router to compute IS-IS SPT using Dijkstra.">
                        <button
                            onClick={() => {
                                const next = !sptMode;
                                setSptMode(next);
                                if (!next) {
                                    setSptResult(null);
                                    setSimulationMode(false);
                                    setExcludedLinks(new Set());
                                    setExcludedNodes(new Set());
                                }
                                if (next) { setPathMode(false); }
                            }}
                            aria-pressed={sptMode}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg
                                border transition-all duration-150 ${
                                    sptMode
                                        ? 'border-semantic-border-brand bg-semantic-bg-brand-primary text-semantic-text-brand'
                                        : 'border-semantic-border-secondary text-semantic-text-secondary hover:text-semantic-text-primary'
                                }`}
                        >
                            {sptResult ? `SPT: ${sptResult.root}` : 'SPT Mode'}
                        </button>
                    </Tooltip>
                    {sptResult && (
                        <Tooltip text="Simulate failures: click routers or links to disable them.">
                            <button
                                onClick={() => {
                                    const next = !simulationMode;
                                    setSimulationMode(next);
                                    if (!next) {
                                        setExcludedLinks(new Set());
                                        setExcludedNodes(new Set());
                                    }
                                }}
                                aria-pressed={simulationMode}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg
                                    border transition-all duration-150 ${
                                        simulationMode
                                            ? 'border-semantic-border-danger bg-semantic-bg-danger-subtle text-semantic-text-danger'
                                            : 'border-semantic-border-secondary text-semantic-text-secondary hover:text-semantic-text-primary'
                                    }`}
                            >
                                {simulationMode
                                    ? `Sim (${excludedLinks.size + excludedNodes.size} off)`
                                    : 'Simulate'}
                            </button>
                        </Tooltip>
                    )}
                    <Tooltip text="Color links by utilization (throughput / capacity). Green < 30%, Yellow 30-70%, Red > 70%.">
                        <button
                            onClick={() => {
                                setTrafficOverlay((v) => !v);
                                setPacketLossOverlay(false);
                            }}
                            aria-pressed={trafficOverlay}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg
                                border transition-all duration-150 ${
                                    trafficOverlay
                                        ? 'border-semantic-border-success bg-semantic-bg-success-subtle text-semantic-text-success'
                                        : 'border-semantic-border-secondary text-semantic-text-secondary hover:text-semantic-text-primary'
                                }`}
                        >
                            Traffic Overlay
                        </button>
                    </Tooltip>
                    <Tooltip text="Color links by packet loss (worst direction). Green ≤ 0.1%, Yellow 0.1–1%, Red > 1%.">
                        <button
                            onClick={() => {
                                setPacketLossOverlay((v) => !v);
                                setTrafficOverlay(false);
                            }}
                            aria-pressed={packetLossOverlay}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg
                                border transition-all duration-150 ${
                                    packetLossOverlay
                                        ? 'border-semantic-border-success bg-semantic-bg-success-subtle text-semantic-text-success'
                                        : 'border-semantic-border-secondary text-semantic-text-secondary hover:text-semantic-text-primary'
                                }`}
                        >
                            Packet Loss
                        </button>
                    </Tooltip>

                    {/* ── Flex-Algo filter ───────────────────────
                        Dropdown shows `locatorName (algorithm)`; the
                        underlying state is the `algorithm` number so
                        labels can change without breaking the filter. */}
                    {availableAlgos.length > 0 && (
                        <div
                            className="flex items-center gap-1 pl-2 ml-1
                                border-l border-semantic-border-secondary"
                        >
                            <Tooltip text="Filter the graph by SRv6 Flex-Algorithm. Dims routers that don't have the algo provisioned.">
                                <select
                                    value={
                                        selectedAlgo == null
                                            ? ''
                                            : String(selectedAlgo)
                                    }
                                    onChange={(e) =>
                                        handleAlgoChange(
                                            e.target.value === ''
                                                ? null
                                                : Number(e.target.value)
                                        )
                                    }
                                    aria-label="Flex-Algo filter"
                                    className={`px-2.5 py-1.5 text-xs font-semibold
                                        rounded-lg border bg-semantic-bg-primary
                                        ${
                                            selectedAlgo != null
                                                ? 'border-semantic-border-brand text-semantic-text-brand'
                                                : 'border-semantic-border-secondary text-semantic-text-secondary hover:text-semantic-text-primary'
                                        }`}
                                >
                                    <option value="">Flex-Algo: All</option>
                                    {availableAlgos.map((a) => (
                                        <option
                                            key={a.algorithm}
                                            value={a.algorithm}
                                        >
                                            {formatAlgoLabel(a)}
                                            {' — '}
                                            {a.nodeCount} node
                                            {a.nodeCount === 1 ? '' : 's'}
                                        </option>
                                    ))}
                                </select>
                            </Tooltip>

                            {selectedAlgo != null && (
                                <div
                                    role="group"
                                    aria-label="Flex-Algo link mode"
                                    className="flex items-center rounded-lg border
                                        border-semantic-border-secondary overflow-hidden"
                                >
                                    {(
                                        [
                                            {
                                                key: 'off',
                                                label: 'Nodes',
                                                tip: 'Dim routers without this flex-algo provisioned. Links stay unchanged.',
                                            },
                                            {
                                                key: 'traffic',
                                                label: 'Traffic',
                                                tip: 'Highlight links with active SRv6 traffic for this algo (SRv6 Demand Matrix / DDM).',
                                            },
                                            {
                                                key: 'spf',
                                                label: 'SPF',
                                                tip: 'Highlight links the flex-algo Shortest Path First tree would use. For min-delay algos (e.g. LATENCY) shows edges that differ from standard IGP; for IGP-based algos shows links between participating routers only.',
                                            },
                                            {
                                                key: 'both',
                                                label: 'Both',
                                                tip: 'Overlay real traffic (solid green) and computed SPF (dashed brand/turquoise) on the same graph.',
                                            },
                                        ] as { key: AlgoLinkMode; label: string; tip: string }[]
                                    ).map((opt) => (
                                        <Tooltip
                                            key={opt.key}
                                            text={opt.tip}
                                            position="bottom"
                                        >
                                            <button
                                                onClick={() =>
                                                    setAlgoLinkMode(opt.key)
                                                }
                                                aria-pressed={
                                                    algoLinkMode === opt.key
                                                }
                                                className={`px-2.5 py-1.5 text-[11px] font-semibold
                                                    transition-colors duration-150
                                                    ${
                                                        algoLinkMode === opt.key
                                                            ? 'bg-semantic-bg-brand-primary text-semantic-text-brand'
                                                            : 'bg-semantic-bg-primary text-semantic-text-secondary hover:text-semantic-text-primary'
                                                    }`}
                                            >
                                                {opt.label}
                                            </button>
                                        </Tooltip>
                                    ))}
                                </div>
                            )}

                            {selectedAlgo != null &&
                                (algoLinkMode === 'traffic' ||
                                    algoLinkMode === 'both') && (
                                    <span
                                        className="text-[10px] font-semibold
                                            text-semantic-text-secondary pl-1"
                                        aria-live="polite"
                                    >
                                        {algoLinksLoading
                                            ? 'Loading…'
                                            : algoLinksError
                                            ? algoLinksError
                                            : algoLinksTraffic
                                            ? `${algoLinksTraffic.links.length} link${algoLinksTraffic.links.length === 1 ? '' : 's'}`
                                            : null}
                                    </span>
                                )}
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'table' && (
                <div className="flex items-center gap-2">
                    <GenericTabs
                        tabs={tableTabsWithTips}
                        activeTab={tableSubTab}
                        onTabChange={(v) => setTableSubTab(v as TableSubTab)}
                        size="xs"
                        fullWidth={false}
                        aria-label="Table sub-views"
                    />
                </div>
            )}
            {activeTab === 'paths' && (
                <div className="flex items-center gap-2">
                    <GenericTabs
                        tabs={pathsTabsWithTips}
                        activeTab={pathsMode}
                        onTabChange={(v) => setPathsMode(v as AnalysisMode)}
                        size="xs"
                        fullWidth={false}
                        aria-label="Path analysis mode"
                    />
                </div>
            )}
            {activeTab === 'matrix' && (
                <div className="flex items-center gap-2">
                    <GenericTabs
                        tabs={matrixTabsWithTips}
                        activeTab={matrixFilter}
                        onTabChange={(v) => setMatrixFilter(v as MatrixFilter)}
                        size="xs"
                        fullWidth={false}
                        aria-label="Matrix filter"
                    />
                </div>
            )}
            {activeTab === 'latency-matrix' && (
                <div className="flex items-center gap-2">
                    <GenericTabs
                        tabs={latencyViewTabsWithTips}
                        activeTab={latencyView}
                        onTabChange={(v) => setLatencyView(v as LatencyMatrixView)}
                        size="xs"
                        fullWidth={false}
                        aria-label="Latency matrix granularity"
                    />
                    {/* Partners only ever see the IGP path, so the site
                        view has no metric to choose. */}
                    {latencyView === 'router' && (
                        <GenericTabs
                            tabs={latencyMetricTabsWithTips}
                            activeTab={latencyMetric}
                            onTabChange={(v) => setLatencyMetric(v as LatencyPathMetric)}
                            size="xs"
                            fullWidth={false}
                            aria-label="Latency path metric"
                        />
                    )}
                </div>
            )}
            {activeTab === 'traffic' && (
                <div className="flex items-center gap-2">
                    <GenericTabs
                        tabs={trafficTabsWithTips}
                        activeTab={trafficSubTab}
                        onTabChange={(v) => setTrafficSubTab(v as TrafficSubTab)}
                        size="xs"
                        fullWidth={false}
                        aria-label="Traffic analysis views"
                    />
                </div>
            )}

            {/* Content area */}
            <div
                className="flex-1 flex overflow-hidden rounded-lg
                    border border-semantic-border-secondary
                    bg-semantic-bg-primary"
                style={{ minHeight: 760 }}
            >
                {/* Graph */}
                {activeTab === 'graph' && (
                    <>
                        <div className="flex-1 min-w-0 relative" style={{ height: '100%' }}>
                            <TopologyGraph
                                data={topologyData}
                                baseline={baselineData}
                                rawBaseline={rawBaselineData}
                                downLinks={downLinks}
                                onNodeClick={handleNodeClick}
                                onEdgeClick={handleEdgeClick}
                                pathMode={pathMode && !demoMode}
                                onPathSelect={handlePathSelect}
                                onSitePathSelect={handleSitePathSelect}
                                linkFields={linkFields}
                                sptResult={sptResult}
                                sptMode={sptMode}
                                onSptRootSelect={handleSptRootSelect}
                                excludedLinks={excludedLinks}
                                excludedNodes={excludedNodes}
                                simulationMode={simulationMode}
                                onToggleExcludeLink={handleToggleExcludeLink}
                                onToggleExcludeNode={handleToggleExcludeNode}
                                trafficOverlay={trafficOverlay}
                                packetLossOverlay={packetLossOverlay}
                                lastUpdate={topologyData.metadata?.timestamp}
                                demoMode={demoMode}
                                onDemoToggle={() => {
                                    if (demoMode) {
                                        handleDemoClose();
                                        setSptMode(true);
                                    } else {
                                        setDemoMode(true);
                                    }
                                }}
                                p2pSource={p2pSource}
                                p2pTarget={p2pTarget}
                                p2pEdgeIds={p2pEdgeIds}
                                p2pNodeIds={p2pNodeIds}
                                onClearP2p={clearP2p}
                                algoHighlight={algoHighlight}
                                algoMap={topologyData.algoMap}
                            />
                            {demoMode && (
                                <SptDemoOverlay
                                    data={topologyData}
                                    onClose={handleDemoClose}
                                    onSptResult={setSptResult}
                                    onSimulationMode={setSimulationMode}
                                    onExcludedLinks={setExcludedLinks}
                                    onExcludedNodes={setExcludedNodes}
                                    onTrafficOverlay={setTrafficOverlay}
                                />
                            )}
                        </div>
                        {selectedNode && !demoMode && (
                            <NodeDetailPanel
                                node={selectedNode}
                                onClose={handleClosePanel}
                                algoMap={topologyData.algoMap}
                            />
                        )}
                        {selectedLink && !demoMode && (
                            <LinkDetailPanel
                                link={selectedLink}
                                baseline={baselineForLink}
                                rawBaseline={rawBaselineForLink}
                                onClose={handleClosePanel}
                            />
                        )}
                    </>
                )}

                {/* Table View */}
                {activeTab === 'table' && (
                    <div className="flex-1 min-w-0 overflow-auto p-4">
                        <TopologyTableView
                            data={topologyData}
                            baseline={baselineData}
                            rawBaseline={rawBaselineData}
                            downLinks={downLinks}
                            onNodeClick={handleNodeClick}
                            onLinkClick={handleEdgeClick}
                            subTab={tableSubTab}
                        />
                    </div>
                )}

                {/* Path Analysis */}
                {activeTab === 'paths' && (
                    <div className="flex-1 min-w-0 overflow-auto">
                        <LatencyPathAnalysis
                            data={topologyData}
                            baseline={baselineData}
                            initialSource={pathSource}
                            initialTarget={pathTarget}
                            initialSiteSrc={pathSiteSrc}
                            initialSiteTgt={pathSiteTgt}
                            mode={pathsMode}
                        />
                    </div>
                )}

                {/* Cost Matrix */}
                {activeTab === 'matrix' && (
                    <div className="flex-1 min-w-0 overflow-auto">
                        <IsisMetricMatrix data={topologyData} filter={matrixFilter} />
                    </div>
                )}

                {/* Latency Matrix */}
                {activeTab === 'latency-matrix' && (
                    <div className="flex-1 min-w-0 overflow-auto">
                        {latencyView === 'router' ? (
                            <LatencyMatrix
                                data={topologyData}
                                baseline={baselineData}
                                metric={latencyMetric}
                            />
                        ) : (
                            <div className="p-4">
                                <SiteLatencyMatrix />
                            </div>
                        )}
                    </div>
                )}

                {/* Traffic (SRv6 content only -- sub-tabs rendered above) */}
                {activeTab === 'traffic' && (
                    <div className="flex-1 min-w-0 overflow-auto p-4 flex flex-col gap-4">
                        {trafficSubTab === 'dashboard' && (
                            <Srv6DashboardTab selectedSource={srv6SelectedSource} timeRange={srv6TimeRange} />
                        )}
                        {trafficSubTab === 'latency' && (
                            <Srv6LatencyTab selectedSource={srv6SelectedSource} timeRange={srv6TimeRange} />
                        )}
                        {trafficSubTab === 'ddm' && (
                            <Srv6DemandMatrixTab
                                timeRange={srv6TimeRange}
                                onViewPath={(src, dst) => setP2pModalPair({ source: src, target: dst })}
                                activePair={p2pModalPair}
                            />
                        )}
                        {trafficSubTab === 'heatmap' && (
                            <Srv6HeatmapTab timeRange={srv6TimeRange} />
                        )}
                    </div>
                )}

                {/* Alerts */}
                {activeTab === 'alerts' && (
                    <div className="flex-1 min-w-0 overflow-auto p-4">
                        <Srv6AlertsTab timeRange={srv6TimeRange} onNavigate={handleAlertNavigate} />
                    </div>
                )}

                {/* Recommendations */}
                {activeTab === 'recommendations' && (
                    <div className="flex-1 min-w-0 overflow-auto p-4">
                        <Srv6RecommendationsTab timeRange={srv6TimeRange} onNavigate={handleAlertNavigate} />
                    </div>
                )}
            </div>

            {/* Detail panels for table tab */}
            {activeTab === 'table' && selectedNode && (
                <div className="fixed top-0 right-0 h-full z-50 shadow-2xl" style={{ marginTop: 0 }}>
                    <NodeDetailPanel
                        node={selectedNode}
                        onClose={handleClosePanel}
                        algoMap={topologyData.algoMap}
                    />
                </div>
            )}
            {activeTab === 'table' && selectedLink && (
                <div className="fixed top-0 right-0 h-full z-50 shadow-2xl" style={{ marginTop: 0 }}>
                    <LinkDetailPanel
                        link={selectedLink}
                        baseline={baselineForLink}
                        rawBaseline={rawBaselineForLink}
                        onClose={handleClosePanel}
                    />
                </div>
            )}

            {/* P2P floating panel (from Demand Matrix "View in topo") */}
            {p2pModalPair && (
                <P2pPanel
                    data={topologyData}
                    baseline={baselineData}
                    source={p2pModalPair.source}
                    target={p2pModalPair.target}
                    onClose={() => setP2pModalPair(null)}
                />
            )}
        </div>
    );
};

export default TopologyPage;
